import { ProxyChallenge } from "../clients/slashid-api"
import {
    ClientGeneratedErrorSlug,
    SerialisedResponseError,
    SlashIDError as SlashIDError_INTERNAL_ONLY,
    SlashIDInternalErrorInput,
    CLIENT_GENERATED_ERROR_SLUGS,
    ERROR_NAMES_TO_SLUGS_MAP,
    isSlashIDError as isSlashIDError_INTERNAL_ONLY,
} from "./errors.internal"

export const ERROR_NAMES = {
    noPasswordSet: "noPasswordSet",
    // TODO not standardised yet
    unknown: "UnexpectedError",
    flowCancelled: "FlowCancelledError",
    handlePatternNotAllowed: "HandlePatternNotAllowedError",
    signUpAwaitingApproval: "SignUpAwaitingApprovalError",
    signInAwaitingApproval: "SignInAwaitingApprovalError",
    selfRegistrationNotAllowed: "SelfRegistrationNotAllowed",
    recoverNonReachableHandleType: "recoverNonReachableHandleType",
    timeoutError: "TimeoutError",
    rateLimitError: "RateLimitError",
    badPasskeyScopeError: "BadPasskeyScopeError",
    passkeyPromptError: "PasskeyPromptError",
    apiResponseError: "APIResponseError",
    invalidSlashIDConfigurationError: "InvalidSlashIDConfigurationError",
    invalidSlashIDURLParamsError: "InvalidSlashIDURLParamsError",
    invalidAuthenticationMethodError: "InvalidAuthenticationMethodError",
    invalidActionError: "InvalidActionError",
    invalidEmailAddressFormatError: "InvalidEmailAddressFormatError",
    invalidPhoneNumberFormatError: "InvalidPhoneNumberFormatError",
    unreachablePhoneNumberError: "UnreachablePhoneNumberError",
    personExistsError: "PersonExistsError",
} as const

export type ErrorName = (typeof ERROR_NAMES)[keyof typeof ERROR_NAMES]

export type FlowCancelledError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.flowCancelled
    context: { challenge: ProxyChallenge }
}

export function isFlowCancelledError(error: any): error is FlowCancelledError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.flowCancelled
}

export type HandlePatternNotAllowedError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.handlePatternNotAllowed
}

export function isHandlePatternNotAllowedError(error: any): error is HandlePatternNotAllowedError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.handlePatternNotAllowed
}

export type SignUpAwaitingApprovalError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.signUpAwaitingApproval
}

export function isSignUpAwaitingApprovalError(error: any): error is SignUpAwaitingApprovalError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.signUpAwaitingApproval
}

export type SignInAwaitingApprovalError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.signInAwaitingApproval
}

export function isSignInAwaitingApprovalError(error: any): error is SignInAwaitingApprovalError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.signInAwaitingApproval
}

export type SelfRegistrationNotAllowedError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.selfRegistrationNotAllowed
}

export function isSelfRegistrationNotAllowedError(
    error: SlashIDError_INTERNAL_ONLY
): error is SelfRegistrationNotAllowedError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.selfRegistrationNotAllowed
}

export type NoPasswordSetError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.noPasswordSet
}

export function isNoPasswordSetError(error: SlashIDError_INTERNAL_ONLY): error is NoPasswordSetError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.noPasswordSet
}

export type NonReachableHandleTypeError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.recoverNonReachableHandleType
}

export function isNonReachableHandleTypeError(error: Error): error is NonReachableHandleTypeError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.recoverNonReachableHandleType
}

export type TimeoutError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.timeoutError
}

export function isTimeoutError(error: any): error is TimeoutError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.timeoutError
}

export type RateLimitError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.rateLimitError
}

export function isRateLimitError(error: any): error is RateLimitError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.rateLimitError
}

export type BadPasskeyScopeError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.badPasskeyScopeError
}

export function isBadPasskeyScopeError(error: any): error is BadPasskeyScopeError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.badPasskeyScopeError
}

export type PasskeyPromptError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.passkeyPromptError
}

export function isPasskeyPromptError(error: any): error is PasskeyPromptError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.passkeyPromptError
}

export type APIResponseError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.apiResponseError
    context: { response: SerialisedResponseError; correlationId: string }
}

export function isAPIResponseError(error: any): error is APIResponseError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.apiResponseError
}

export type InvalidSlashIDConfigurationError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.invalidSlashIDConfigurationError
}

export function isInvalidSlashIDConfigurationError(error: any): error is InvalidSlashIDConfigurationError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.invalidSlashIDConfigurationError
}

export type InvalidURLParamsError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.invalidSlashIDURLParamsError
}

export function isInvalidURLParamsError(error: any): error is InvalidURLParamsError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.invalidSlashIDURLParamsError
}

export type InvalidAuthenticationMethodError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.invalidAuthenticationMethodError
}

export function isInvalidAuthenticationMethodError(error: any): error is InvalidAuthenticationMethodError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.invalidAuthenticationMethodError
}

// this error type is thrown whenever an SDK action is attempted while in invalid state
export type InvalidActionError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.invalidActionError
}

export function isInvalidActionError(error: any): error is InvalidActionError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.invalidActionError
}

export type PersonExistsError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.personExistsError
}

export function isPersonExistsError(error: any): error is PersonExistsError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.personExistsError
}

export type InvalidEmailAddressFormatError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.invalidEmailAddressFormatError
}

export function isInvalidEmailAddressFormatError(error: any): error is InvalidEmailAddressFormatError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.invalidEmailAddressFormatError
}

export type InvalidPhoneNumberFormatError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.invalidPhoneNumberFormatError
}

export function isInvalidPhoneNumberFormatError(error: any): error is InvalidPhoneNumberFormatError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.invalidPhoneNumberFormatError
}

export type UnreachablePhoneNumberError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.unreachablePhoneNumberError
}

export function isUnreachablePhoneNumberError(error: any): error is UnreachablePhoneNumberError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.unreachablePhoneNumberError
}

export type UnexpectedError = SlashIDError_INTERNAL_ONLY & {
    name: typeof ERROR_NAMES.unknown
}

export function isUnexpectedError(error: any): error is UnexpectedError {
    return error instanceof SlashIDError_INTERNAL_ONLY && error.name === ERROR_NAMES.unknown
}

export type SlashIDError =
    | FlowCancelledError
    | SelfRegistrationNotAllowedError
    | NoPasswordSetError
    | NonReachableHandleTypeError
    | HandlePatternNotAllowedError
    | SignUpAwaitingApprovalError
    | SignInAwaitingApprovalError
    | TimeoutError
    | RateLimitError
    | BadPasskeyScopeError
    | InvalidSlashIDConfigurationError
    | InvalidURLParamsError
    | InvalidAuthenticationMethodError
    | PasskeyPromptError
    | InvalidActionError
    | PersonExistsError
    | InvalidEmailAddressFormatError
    | InvalidPhoneNumberFormatError
    | UnreachablePhoneNumberError
    | APIResponseError
    | UnexpectedError

export type SlashIDErrorInput = SlashIDInternalErrorInput & {
    name: SlashIDError["name"]
}

// has a more strict input interface than the internal error, only allows the public error types
export function createSlashIDError({ message, name, cause, context }: SlashIDErrorInput): SlashIDError {
    const error = new SlashIDError_INTERNAL_ONLY({ message, name, cause, context })

    // we know this must be a SlashIDError as we created it
    return error as SlashIDError
}

export function isSlashIDError(error: any): error is SlashIDError {
    return isSlashIDError_INTERNAL_ONLY(error)
}

// Console charts depend on this
export const isClientGeneratedErrorSlug = (text: string): text is ClientGeneratedErrorSlug => {
    return (CLIENT_GENERATED_ERROR_SLUGS as readonly string[]).includes(text)
}

// re-export for convenience, to be used in the Console
export { CLIENT_GENERATED_ERROR_SLUGS, ERROR_NAMES_TO_SLUGS_MAP }
export type { ClientGeneratedErrorSlug }
